// In-house COVID / STREP / FLU results -> Athena, at most once per test (2026-10-06).
//
// "Mark as Complete" used to render and upload every selected test on every click:
// a second click (or the dashboard and the tasks page both completing the task) filed
// the same result again (13300: 5 images for 3 tests; 10753 and 6473: 6 for 3), and
// Athena keeps every copy. Now:
//   * the upload names its task (orderid + its order date), so the gateway files one
//     document per test and answers a repeat with the copy already in Athena;
//   * a gateway that keys on the task (it answers mark_as_complete with
//     newly_completed_ids) gets EVERY selected in-house test, so a result whose upload
//     failed (Athena error, no answer, tab closed) is sent again by selecting the
//     completed task and clicking again - it is never stuck (review 10-06);
//   * an older gateway (no newly_completed_ids) only gets tests this click completed,
//     because it would file a repeat again;
//   * a report that did not render is not uploaded (a blank first copy would be kept
//     as "the" result);
//   * a task already being rendered/sent from this tab is not sent again;
//   * the caller gets every outcome and shows one summary (failures in red).
import dayjs from "dayjs";
import html2canvas from "html2canvas";
import api from "../api/api";

export const IN_HOUSE_TESTS = Object.freeze(["COVID TEST", "STREP TEST", "FLU TEST"]);

export const isInHouseTest = (task) =>
  !!task && IN_HOUSE_TESTS.includes(String(task.ordername || "").trim().toUpperCase());

// Task (orders row) ids whose report is being rendered or sent from this tab.
const inFlight = new Set();

export const isUploadInFlight = (taskId) => inFlight.has(String(taskId));

/**
 * Which of `ids` this completion actually completed.
 * response: the mark_as_complete answer (api.js returns the body). Its
 * newly_completed_ids is authoritative when present (an array, possibly empty);
 * otherwise (older server, or it could not tell) the tasks that were not
 * COMPLETED before the click count.
 */
export const newlyCompletedIds = (ids, response, tasks) => {
  const list = (ids || []).map(String);
  const fromServer = response && Array.isArray(response.newly_completed_ids)
    ? new Set(response.newly_completed_ids.map(String))
    : null;
  if (fromServer) return list.filter((id) => fromServer.has(id));
  return list.filter((id) => {
    const t = (tasks || []).find((x) => String(x?.id) === id);
    return !t || t.status !== "COMPLETED";
  });
};

/** True when the mark_as_complete answer comes from a gateway that files in-house results
 * once per task (it reports newly_completed_ids), so re-sending a completed test is safe. */
export const serverDedupesByTask = (response) => !!response && Array.isArray(response.newly_completed_ids);

/** The in-house tests among `ids` (looked up in `tasks`) that this click should file. */
export const inHouseTasksToUpload = (ids, response, tasks) =>
  (serverDedupesByTask(response) ? (ids || []).map(String) : newlyCompletedIds(ids, response, tasks))
    .map((id) => (tasks || []).find((t) => String(t?.id) === id))
    .filter((t) => isInHouseTest(t) && !isUploadInFlight(t.id));

/** The task's own order date (YYYY-MM-DD): picks orders_master vs orders_master_history. */
export const taskOrderDate = (task) => {
  const d = task?.ts ? dayjs(task.ts) : null;
  return d && d.isValid() ? d.format("YYYY-MM-DD") : "";
};

/** The preview page that renders this task's report. */
export const inHouseReportUrl = (task, origin = window.location.origin) => {
  const day = taskOrderDate(task);
  return `${origin}/imaging/strep-covid-flu-Preview/${task.id}` + (day ? `?orderdate=${day}` : "");
};

// Renders the preview page in a hidden same-origin iframe and captures the report as a
// JPG blob. Throws when the report never renders (no blank page goes to Athena).
async function renderInHouseReport(task) {
  const fileUrl = inHouseReportUrl(task);
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.top = "-10000px";
  iframe.style.left = "-10000px";
  iframe.style.width = "1200px";
  iframe.style.height = "900px";
  iframe.style.border = "none";
  document.body.appendChild(iframe);
  try {
    await new Promise((resolve, reject) => {
      iframe.onload = resolve;
      iframe.onerror = () => reject(new Error("Failed to load preview page"));
      iframe.src = fileUrl;
    });
    // Wait for the report to be fully rendered (it gets id="report-ready")
    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    let reportElement = null;
    for (let i = 0; i < 20; i++) {
      reportElement = iframeDoc.getElementById("report-ready");
      if (reportElement) break;
      await new Promise((res) => setTimeout(res, 500));
    }
    if (!reportElement) {
      // the old code captured the bare page here and filed a blank report
      throw new Error("the report did not load, so nothing was sent - try again");
    }
    const canvas = await html2canvas(reportElement, {
      useCORS: true,
      allowTaint: true,
      scale: 2,
      backgroundColor: "#ffffff",
    });
    return await new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.95));
  } finally {
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  }
}

/**
 * Render one in-house test's report and file it in Athena (once per task).
 * Returns the gateway's answer, or {skipped: "in_flight"} when this tab is already
 * sending that task. Errors propagate (the caller toasts them). `render` is the
 * report renderer (tests pass a stub; the default renders the preview page).
 */
export async function uploadInHouseResult({ practiseid, patientid, task, departmentid = 1,
                                           render = renderInHouseReport }) {
  const key = String(task.id);
  if (inFlight.has(key)) return { skipped: "in_flight" };
  inFlight.add(key);
  try {
    const fileBlob = await render(task);
    const formData = new FormData();
    formData.append("file", fileBlob, "file.jpg");
    formData.append("attachmenttype", "JPG");
    formData.append("observationdate", dayjs().format("MM/DD/YYYY"));
    formData.append("observationtime", dayjs().format("HH:mm"));
    formData.append("autoclose", true);
    formData.append("orderid", key);
    const day = taskOrderDate(task);
    if (day) formData.append("orderdate", day);
    return await api.post(
      `/v1/${practiseid}/${departmentid}/patient/${patientid}/add-strepcovidflu-athena`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
  } finally {
    inFlight.delete(key);
  }
}

/** Toast text for one upload answer. */
export const inHouseUploadMessage = (task, answer) => {
  const name = task?.ordername || "Test";
  if (answer?.skipped === "in_flight") return `${name}: already being sent to Athena`;
  if (answer?.duplicate_prevented && answer?.status === 202) return `${name}: already being sent to Athena`;
  if (answer?.duplicate_prevented) return `${name}: already in Athena - not sent again`;
  if (answer?.status === 200) return `${name}: result sent to Athena`;
  return `${name}: ${answer?.message || "sent to Athena"}`;
};

/** Toast text for one failed upload (the gateway's message when it sent one). */
export const inHouseErrorMessage = (task, error) => {
  const d = error?.response?.data;
  const msg = (d && typeof d === "object" && typeof d.message === "string" && d.message) || error?.message || "upload failed";
  return `${task?.ordername || "Test"}: NOT sent to Athena - ${msg}`;
};

/**
 * File each task's result, one after the other; never throws. Returns
 * {lines, failed}: one line per task, and how many did not reach Athena (an error,
 * no answer, or a declined confirm) - the caller shows them all in one toast.
 */
export async function sendInHouseResults({ practiseid, tasks, fallbackPatientid, upload = uploadInHouseResult }) {
  const lines = [];
  let failed = 0;
  for (const t of tasks || []) {
    try {
      const answer = await upload({ practiseid, patientid: t.patientid || fallbackPatientid, task: t });
      lines.push(inHouseUploadMessage(t, answer));
    } catch (error) {
      failed += 1;
      lines.push(inHouseErrorMessage(t, error));
    }
  }
  return { lines, failed };
}

/** One toast for a completion and its uploads: {severity, message}. */
export const inHouseSummaryToast = (completionMessage, result) => {
  const lines = [completionMessage, ...((result && result.lines) || [])].filter(Boolean);
  const failed = (result && result.failed) || 0;
  if (failed) {
    lines.push("Select the test again and click Mark as Complete to resend it.");
  }
  return { severity: failed ? "error" : "success", message: lines.join(" | ") };
};
